import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Package } from "lucide-react";
import Layout from "@/components/Layout";
import api, { formatINR } from "@/lib/api";

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then(r => setOrder(r.data)).catch(() => {});
  }, [id]);

  return (
    <Layout>
      <div className="max-w-[800px] mx-auto px-6 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 mx-auto text-[#C9A227]" strokeWidth={1.2} />
        <div className="overline text-[#C9A227] mt-6">Thank you</div>
        <h1 className="mt-3 text-4xl md:text-5xl font-light text-[#163A2F] tracking-tight">
          Your order is <span className="italic text-[#C9A227]">confirmed.</span>
        </h1>
        <p className="mt-4 text-[#163A2F]/70 font-light">A confirmation has been sent to your email.</p>

        {order && (
          <div className="mt-10 text-left border hairline p-6 md:p-8">
            <div className="flex justify-between flex-wrap gap-2">
              <div className="overline text-[#C9A227]">Order #</div>
              <div data-testid="order-number" className="font-medium text-[#163A2F]">{order.order_number}</div>
            </div>
            <div className="mt-2 flex justify-between"><span className="text-[#163A2F]/70 text-sm">Status</span><span className="text-sm font-medium text-[#163A2F] uppercase">{order.status}</span></div>
            <div className="my-5 gold-rule" />
            <div className="space-y-4">
              {order.items.map((i, idx) => (
                <div key={idx} className="flex gap-3">
                  <img src={i.image} alt={i.name} className="w-14 h-18 object-cover bg-[#163A2F]/5" />
                  <div className="flex-1 text-sm">
                    <div className="text-[#163A2F]">{i.name}</div>
                    <div className="text-[#163A2F]/60 text-xs">{i.size} · {i.color || "Default"} · Qty {i.quantity}</div>
                  </div>
                  <div className="text-sm text-[#163A2F]">{formatINR(i.price * i.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="my-5 gold-rule" />
            <div className="flex justify-between text-[#163A2F]"><span>Subtotal</span><span>{formatINR(order.subtotal)}</span></div>
            {order.discount > 0 && (
              <div className="flex justify-between text-[#C9A227]">
                <span>Discount {order.coupon_code ? `(${order.coupon_code})` : ""}</span>
                <span>− {formatINR(order.discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-[#163A2F]"><span>Shipping</span><span>{formatINR(order.shipping_cost)}</span></div>
            <div className="mt-2 flex justify-between text-lg font-medium text-[#163A2F]"><span>Total</span><span>{formatINR(order.total)}</span></div>

            <div className="mt-6 text-sm text-[#163A2F]/70">
              <div className="flex items-center gap-2 text-[#163A2F]"><Package className="w-4 h-4" /> Shipping to</div>
              <div className="mt-1">{order.address.full_name}, {order.address.line1}, {order.address.city}, {order.address.state} — {order.address.pincode}</div>
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link to="/account" data-testid="view-orders" className="bg-[#163A2F] text-white px-8 py-4 uppercase text-xs tracking-[0.25em]">Track order</Link>
          <Link to="/shop" className="border border-[#163A2F] text-[#163A2F] px-8 py-4 uppercase text-xs tracking-[0.25em]">Continue shopping</Link>
        </div>
      </div>
    </Layout>
  );
}
